import { computed, watch, type Ref } from "vue";
import { useRoute } from "vue-router";
import {
  handleAuthCallback,
  type ExtensionAuthOptions,
} from "@/features/extension-auth/extensionAuth";
import { useStore } from "vuex";

interface ExtensionAuthCallbackReturn {
  handleAuthCallback: () => Promise<void>;
  startExtensionAuth: (_options?: { silent?: boolean }) => Promise<void>;
  isExtensionAuthenticated: Ref<boolean>;
}

export function useExtensionAuthCallback(): ExtensionAuthCallbackReturn {
  const route = useRoute();
  const store = useStore();

  const extensionAuth = computed(() => store.state.authentication.extension);
  const isExtensionAuthenticated = computed(
    () => !!extensionAuth.value?.access_token
  );

  const initiateAuth = async (
    options?: ExtensionAuthOptions
  ): Promise<void> => {
    return store.dispatch("authentication/initiateExtensionAuth", options);
  };

  const handleAuthCallbackWrapper = () => handleAuthCallback(route.query);

  const startExtensionAuth = async (options?: {
    silent?: boolean;
  }): Promise<void> => {
    let stopWatcher: (() => void) | null = null;

    try {
      await initiateAuth(options);

      if (!isExtensionAuthenticated.value) {
        stopWatcher = watch(
          isExtensionAuthenticated,
          (newValue) => {
            if (newValue) {
              stopWatcher?.();
            }
          },
          { immediate: false }
        );
      }
    } catch (error) {
      if (stopWatcher) {
        stopWatcher();
      }
      throw error;
    }
  };

  return {
    handleAuthCallback: handleAuthCallbackWrapper,
    startExtensionAuth,
    isExtensionAuthenticated,
  };
}
